import { z } from "zod";

/**
 * ONBOARDING_INTENT_KEY is the sessionStorage key holding the post-signup onboarding
 * intent. sessionStorage (not localStorage) scopes the intent to the signup tab and
 * clears it when the tab closes, and it survives the full-page navigations the auth
 * flow makes (window.location.assign on registration success, or an OAuth round-trip)
 * where React state would be lost.
 */
const ONBOARDING_INTENT_KEY = "mountaya.onboarding.intent";

/**
 * CREATE_ORGANIZATION_INTENT marks a user who chose "I represent an organization" at
 * signup, so their first authenticated landing routes into organization creation.
 */
const CREATE_ORGANIZATION_INTENT = "create-organization";

/**
 * onboardingIntentSchema validates the stored value, so a stale or tampered entry
 * never reads as a live intent.
 */
const onboardingIntentSchema = z.literal(CREATE_ORGANIZATION_INTENT);

/**
 * sessionStore returns the tab's sessionStorage, or undefined when it is unavailable
 * (server prerender, or a browser that throws on storage access in private mode). The
 * onboarding intent is a best-effort UX hint, so callers degrade silently rather than
 * throwing.
 */
const sessionStore = (): Storage | undefined => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
};

/**
 * setOnboardingIntent records that the new user wants to create an organization right
 * after signing up. Best-effort: a storage failure is swallowed, the flow just lands
 * the user in the app instead.
 */
export const setOnboardingIntent = (): void => {
  try {
    sessionStore()?.setItem(ONBOARDING_INTENT_KEY, CREATE_ORGANIZATION_INTENT);
  } catch {
    // Best-effort: see sessionStore. A write failure is non-fatal.
  }
};

/**
 * clearOnboardingIntent removes the stored intent. Called when an unchecked sign-up
 * supersedes a prior toggle, and implicitly by consumeOnboardingIntent.
 */
export const clearOnboardingIntent = (): void => {
  try {
    sessionStore()?.removeItem(ONBOARDING_INTENT_KEY);
  } catch {
    // Best-effort: see sessionStore.
  }
};

/**
 * peekOnboardingIntent reports whether a create-organization intent is pending without
 * clearing it. Read-only, so it is safe to call from a route preload (a hover prefetch)
 * where a side effect must not fire. Best-effort: a storage read that throws (private
 * mode) reads as no pending intent rather than propagating.
 */
export const peekOnboardingIntent = (): boolean => {
  try {
    return onboardingIntentSchema.safeParse(sessionStore()?.getItem(ONBOARDING_INTENT_KEY)).success;
  } catch {
    return false;
  }
};

/**
 * consumeOnboardingIntent reads the pending intent and clears it in one shot, so the
 * post-signup redirect fires exactly once. Returns whether an intent was present.
 */
export const consumeOnboardingIntent = (): boolean => {
  const pending = peekOnboardingIntent();
  if (pending) {
    clearOnboardingIntent();
  }

  return pending;
};
